import React from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  ArrowRight,
  BadgePercent,
  CheckCircle2,
  Handshake,
  LayoutDashboard,
  Rocket,
  Sparkles,
  Ticket,
  TrendingUp,
  UserPlus,
} from "lucide-react"
import Navbar from "../../components/LandPage/Navbar"
import Footer from "../../components/LandPage/Footer"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import {
  PublicAnimatedBackground,
  publicAnimatedBackgroundShellClassName,
} from "../../components/PublicAnimatedBackground/PublicAnimatedBackground"
import { PATH_MAP } from "../../configuration/PathMap"
import { SEO } from "../../seo/Seo"
import { organizationSchema, softwareSchema } from "../../seo/schema"

const stepIcons = [UserPlus, Handshake, CheckCircle2, TrendingUp]

const benefitIcons = [UserPlus, BadgePercent, LayoutDashboard, Ticket]

const growthIcons = [Rocket, TrendingUp, Sparkles]

export const ResellerLandingPage: React.FC = () => {
  const { t } = useTranslation("reseller")

  const navigate = useNavigate()

  const handleGoToSignUp = () => navigate(PATH_MAP.RESELLER_SIGNUP_PAGE)

  const handleGoToLogin = () => navigate(PATH_MAP.RESELLER_LOGIN_PAGE)

  const stats = [
    { value: t("landing.stat_signup_value"), label: t("landing.stat_signup_label") },
    { value: t("landing.stat_commission_value"), label: t("landing.stat_commission_label") },
    { value: t("landing.stat_visibility_value"), label: t("landing.stat_visibility_label") },
  ]

  const steps = [
    { title: t("landing.step_1_title"), description: t("landing.step_1_description") },
    { title: t("landing.step_2_title"), description: t("landing.step_2_description") },
    { title: t("landing.step_3_title"), description: t("landing.step_3_description") },
    { title: t("landing.step_4_title"), description: t("landing.step_4_description") },
  ]

  const benefits = [
    { title: t("landing.benefit_signup_title"), description: t("landing.benefit_signup_description") },
    { title: t("landing.benefit_commission_title"), description: t("landing.benefit_commission_description") },
    { title: t("landing.benefit_dashboard_title"), description: t("landing.benefit_dashboard_description") },
    { title: t("landing.benefit_coupon_title"), description: t("landing.benefit_coupon_description") },
  ]

  const growthTiers = [
    { title: t("landing.growth_tier_1_title"), description: t("landing.growth_tier_1_description") },
    { title: t("landing.growth_tier_2_title"), description: t("landing.growth_tier_2_description") },
    { title: t("landing.growth_tier_3_title"), description: t("landing.growth_tier_3_description") },
  ]

  return (
    <div
      className={`${publicAnimatedBackgroundShellClassName} min-h-screen overflow-x-hidden bg-white text-slate-950`}
    >
      <PublicAnimatedBackground baseColor="#ffffff" />

      <SEO
        title={t("landing.hero_title")}
        description={t("landing.hero_subtitle")}
        path={PATH_MAP.RESELLER_LANDING_PAGE}
        structuredData={[organizationSchema, softwareSchema]}
      />

      <Animate type={AnimationTypes.slideDownIn} duration="0.9s">
        <Navbar />
      </Animate>

      <main className="mt-16 pt-8 pb-20 lg:pt-14">
        <Animate type={AnimationTypes.fadeInTraslate} duration="1.1s">
            <section className="mx-auto max-w-[1100px] px-4 text-center lg:px-6">
            <span className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-1.5 text-xs font-black uppercase tracking-wide text-orange-700">
              <Sparkles size={14} />
              {t("landing.badge")}
            </span>

            <h1 className="mx-auto mt-5 max-w-3xl text-3xl font-black leading-[1.15] text-slate-950 sm:text-4xl lg:text-5xl">
              {t("landing.hero_title")}
            </h1>

            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-600 lg:text-lg">
              {t("landing.hero_subtitle")}
            </p>


          <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s" delay="0.1s">
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleGoToSignUp}
                className="inline-flex min-h-13 cursor-pointer items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-8 py-4 text-base font-black text-white shadow-lg shadow-red-500/30 transition hover:scale-[1.02] active:scale-95"
                data-interaction-description-key="interactionDescriptions:pages.landPage.resellerLandingPage.button.signup"
              >
                {t("landing.hero_cta")}
                <ArrowRight size={18} />
              </button>

              <button
                type="button"
                onClick={handleGoToLogin}
                className="inline-flex min-h-13 cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-8 py-4 text-base font-black text-slate-900 transition hover:bg-slate-50 active:scale-95"
                data-interaction-description-key="interactionDescriptions:pages.landPage.resellerLandingPage.button.login"
              >
                {t("landing.hero_login_link")}
              </button>
            </div>
          </Animate>

          <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s" delay="0.1s">
            <p className="mt-4 text-xs font-bold uppercase tracking-wide text-slate-400">{t("landing.hero_note")}</p>
          </Animate>

          <Animate type={AnimationTypes.fadeInTraslate} duration="1.6s" delay="0.2s">
            <div className="mx-auto mt-12 grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-3">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-2xl border border-slate-200 bg-white/80 p-5 shadow-sm backdrop-blur-sm"
                >
                  <p className="bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-2xl font-black text-transparent">
                    {stat.value}
                  </p>

                  <p className="mt-1 text-xs leading-relaxed font-semibold text-slate-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </Animate>
        </section>
        </Animate>
        <section className="mx-auto mt-24 max-w-[1100px] px-4 lg:px-6">
          <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
            <div className="text-center">
              <h2 className="text-2xl font-black text-slate-950 sm:text-3xl">{t("landing.how_it_works_title")}</h2>

              <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500 sm:text-base">
                {t("landing.how_it_works_subtitle")}
              </p>
            </div>
          </Animate>

          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, index) => {
              const StepIcon = stepIcons[index % stepIcons.length]

              return (
                <Animate key={step.title} type={AnimationTypes.slideUpIn} duration="1s" delay={`${index * 0.12}s`}>
                  <div className="relative h-full rounded-2xl border border-slate-200 bg-white p-5">
                    <span className="absolute -top-3 -left-3 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-red-600 to-orange-500 text-xs font-black text-white shadow-md">
                      {index + 1}
                    </span>

                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
                      <StepIcon size={20} />
                    </div>

                    <h3 className="mt-4 font-black text-slate-950">{step.title}</h3>

                    <p className="mt-2 text-sm leading-relaxed text-slate-500">{step.description}</p>
                  </div>
                </Animate>
              )
            })}
          </div>
        </section>

        <section className="mx-auto mt-24 max-w-[1100px] px-4 lg:px-6">
          <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
            <h2 className="text-center text-2xl font-black text-slate-950 sm:text-3xl">
              {t("landing.benefits_title")}
            </h2>
          </Animate>

          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {benefits.map((benefit, index) => {
              const BenefitIcon = benefitIcons[index % benefitIcons.length]

              return (
                <Animate key={benefit.title} type={AnimationTypes.slideUpIn} duration="1s" delay={`${index * 0.12}s`}>
                  <div className="flex h-full items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-orange-600 shadow-sm">
                      <BenefitIcon size={20} />
                    </div>

                    <div>
                      <h3 className="font-black text-slate-950">{benefit.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{benefit.description}</p>
                    </div>
                  </div>
                </Animate>
              )
            })}
          </div>
        </section>

        <section className="mx-auto mt-24 max-w-[1100px] px-4 lg:px-6">
          <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
            <div className="rounded-3xl border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-amber-50 p-6 sm:p-10">
              <div className="text-center">
                <h2 className="text-2xl font-black text-slate-950 sm:text-3xl">{t("landing.growth_title")}</h2>

                <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600 sm:text-base">
                  {t("landing.growth_subtitle")}
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
                {growthTiers.map((tier, index) => {
                  const GrowthIcon = growthIcons[index % growthIcons.length]

                  return (
                    <Animate
                      key={tier.title}
                      type={AnimationTypes.fadeInTraslate}
                      duration="1s"
                      delay={`${index * 0.12}s`}
                    >
                      <div className="h-full rounded-2xl bg-white p-5 shadow-sm">
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-100 text-orange-600">
                          <GrowthIcon size={20} />
                        </div>

                        <h3 className="mt-4 font-black text-slate-950">{tier.title}</h3>

                        <p className="mt-2 text-sm leading-relaxed text-slate-500">{tier.description}</p>
                      </div>
                    </Animate>
                  )
                })}
              </div>
            </div>
          </Animate>
        </section>


        <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
            <section className="mx-auto mt-24 max-w-[900px] px-4 text-center lg:px-6">
                <h2 className="text-2xl font-black text-slate-950 sm:text-3xl">{t("landing.final_cta_title")}</h2>

                <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500 sm:text-base">
                  {t("landing.final_cta_subtitle")}
                </p>

                <button
                  type="button"
                  onClick={handleGoToSignUp}
                  className="mt-7 inline-flex min-h-13 cursor-pointer items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-8 py-4 text-base font-black text-white shadow-lg shadow-red-500/30 transition hover:scale-[1.02] active:scale-95"
                  data-interaction-description-key="interactionDescriptions:pages.landPage.resellerLandingPage.button.finalCta"
                >
                  {t("landing.final_cta_button")}
                  <ArrowRight size={18} />
                </button>
            </section>
        </Animate>
      </main>

      <Footer />
    </div>
  )
}
